import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Textarea } from "@component/textarea";
import { Switch } from "@component/switch";
import {
    ImageIcon,
    PaletteIcon,
    CheckIcon,
    EyeIcon,
    InfoIcon,
} from "lucide-react";
import { useSnackbar } from "@hook/use-snackbar";
import { updateEmployeerProfile } from "@service/auth";
import { createInterviewContext } from "@service/interview";
import { useUserProfileContext } from "@context/user_profile";
import { Company, SubCompany } from "@type/company";
import { updatePreferenceCompanyLogo } from "@service/user";
import { Tooltip } from "@component/tooltip";
import { useCompanySubdomain } from "@context/subdomain_context";
import PreviewWaitingRoom from "@component/preview-waiting-room";
import SettingsTwoPanelLayout from "@component/setting-two-panel";
import SettingCompanyDepartmentSection from "./setting-company-department";

interface Props {
    company: Company | undefined;
}

export default function CompanyInformationSectionV2(props: Props) {
    const snackbar = useSnackbar();
    const initialFormData = {
        companyName: props.company?.company_name || "",
        companyWebsite: props.company?.company_website || "",
        companyLinkedIn: props.company?.company_linkedin || "",
        description: props.company?.company_description || "",
        showBranding: props.company?.is_show_brand || false,
        company_primary_color: props.company?.preferences?.primary_color || "#2563eb",
        companies: props.company?.company_metadata?.companies || [],
    };
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [isLoadingCompanyInfo, companyInfo] = useCompanySubdomain();
    const [isLoadingSetting, setLoadingSetting] = useState(false);
    const [formData, setFormData] = useState(initialFormData);
    const [logoPreview, setLogoPreview] = useState<string | null>(props.company?.preferences?.company_logo || null);
    const [showBranding, setShowBranding] = useState<boolean>(props.company?.is_show_brand || false);
    const [generatedColors, setGeneratedColors] = useState<string[]>([]);
    const [selectedAccentColor, setSelectedAccentColor] = useState<
        string | null
    >(null);
    const [customColor, setCustomColor] = useState<string>(props.company?.preferences?.primary_color || "#2563eb");
    const [showCustomColorPicker, setShowCustomColorPicker] =
        useState<boolean>(false);
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!isLoadingCompanyInfo && companyInfo) {
            setFormData((prev) => ({
                ...prev,
                companies: companyInfo?.company_metadata?.companies || [],
            }));
        }
    }, [isLoadingCompanyInfo, companyInfo]);

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const companiesChanges = (subcompanies: SubCompany[]) => {
        console.log('subcompanies updated:', subcompanies);
        setFormData((prev) => ({
            ...prev,
            companies: subcompanies,
        }));
    }

    // Check if form has changes
    const hasChanges = useMemo(() => {
        return formData.companyName !== initialFormData.companyName || formData.companyWebsite !== initialFormData.companyWebsite || formData.companyLinkedIn !== initialFormData.companyLinkedIn || formData.description !== initialFormData.description || showBranding !== initialFormData.showBranding || customColor !== initialFormData.company_primary_color || JSON.stringify(formData.companies) !== JSON.stringify(initialFormData.companies);
    }, [formData, showBranding, customColor]);

    const handleSave = async () => {
        setLoadingSetting(true);
        try {
            await updateEmployeerProfile({
                username: userInfo?.username || "",
                first_name: userInfo?.first_name || "",
                last_name: userInfo?.last_name || "",
                company_name: formData.companyName,
                company_linkedin: formData.companyLinkedIn,
                company_description: formData.description,
                company_website: formData.companyWebsite,
                is_show_brand: showBranding,
                company_primary_color: customColor,
                companies: formData.companies || [],
            })

            if (formData.description && userInfo?.company_id) {
                createInterviewContext(userInfo.company_id, formData.description).catch((err) => {
                    console.error("Error saving interview context:", err);
                });
            }

            snackbar.showSuccess("Company information saved successfully!");
        } catch (error) {
            snackbar.showError("Failed to save company information.");
            console.error("Error saving company information:", error);
        } finally {
            setLoadingSetting(false);
        }
    };

    const handleLogoUpload = async (file: File) => {
        try {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            const resp = await updatePreferenceCompanyLogo(file);
            if (resp.status === 200) {
                setLogoPreview(resp.data.data.company_logo_path);
                const mockColors = [
                    "#2563eb", // Blue
                    "#7c3aed", // Purple
                    "#059669", // Green
                    "#dc2626", // Red
                    "#ea580c", // Orange
                    "#0891b2", // Cyan
                ];

                setGeneratedColors(mockColors);
                snackbar.showSuccess("Logo uploaded successfully!");
            }
        } catch (error) {
            snackbar.showError("Failed to upload logo.");
            console.error("Error uploading logo:", error);
        }
    };

    const handleColorSelect = (color: string) => {
        setSelectedAccentColor(color);
        handleCustomColorChange(color);
    };

    const handleCustomColorChange = (color: string) => {
        setCustomColor(color);
        setSelectedAccentColor(color);
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    // Check if all companies have at least one department
    const isCompanyHasDepartment = useMemo(() => {
        return (formData.companies || []).length > 0 ? (formData.companies || []).every((company) => company.departments && company.departments.length > 0) : false;
    }, [formData.companies]);

    return (
        <div className="space-y-8">
            {/* Company Information Section */}
            <SettingsTwoPanelLayout
                title="Company Information"
                description="Basic information about your company."
            >
                <div className="space-y-6">
                    {/* Company Logo Upload */}
                    <div className="space-y-3" style={{ paddingBottom: "16px" }}>
                        <Label
                            className="text-sm font-medium text-gray-700"
                            style={{ color: "#000000" }}
                        >
                            Company Logo
                        </Label>
                        <div className="flex items-center space-x-4">
                            <div
                                className="min-w-24 h-24 border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 cursor-pointer hover:border-gray-400 transition-colors"
                                style={{ border: "1px solid rgb(209, 213, 219)" }}
                                onClick={triggerFileInput}
                            >
                                {logoPreview ? (
                                    <img
                                        src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}${logoPreview ? `/${logoPreview}` : '/commons/ourteam_logo_only.png'}`}
                                        alt="Company Logo"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <ImageIcon className="h-5 w-5 text-gray-400" />
                                )}
                            </div>
                            <div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            handleLogoUpload(e.target.files[0]);
                                        }
                                    }}
                                    className="hidden"
                                />

                                <Button
                                    variant="outline"
                                    className="text-sm h-10"
                                    onClick={triggerFileInput}
                                >
                                    Upload logo
                                </Button>
                                <p className="text-sm text-gray-500 mt-1">
                                    Max. 5MB, PNG only.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Company Details */}
                    <div className="space-y-4">
                        <div className="space-y-2" style={{ paddingBottom: "16px" }}>
                            <div className="flex items-center gap-2">
                                <Label
                                    htmlFor="companyName"
                                    className="text-sm font-medium text-gray-700"
                                    style={{ color: "#000000" }}
                                >
                                    Company Name
                                </Label>
                                {props.company?.is_byoc && (
                                    <Tooltip
                                        content={
                                            <p>Your company name will be shown to candidates in each of your company job listings.</p>
                                        }
                                        side="right"
                                        delayDuration={0}
                                    >
                                        <InfoIcon className="h-4 w-4 text-blue-500 cursor-help" />
                                    </Tooltip>
                                )}
                            </div>
                            <Input
                                id="companyName"
                                value={formData.companyName}
                                onChange={(e) =>
                                    handleInputChange("companyName", e.target.value)
                                }
                                className="w-full"
                            />
                        </div>

                        <div
                            className="space-y-2"
                            style={{ paddingTop: "0px", paddingBottom: "16px" }}
                        >
                            <Label
                                htmlFor="companyWebsite"
                                className="text-sm font-medium text-gray-700"
                                style={{ color: "#000000" }}
                            >
                                Company Website
                            </Label>
                            <Input
                                id="companyWebsite"
                                value={formData.companyWebsite}
                                onChange={(e) =>
                                    handleInputChange("companyWebsite", e.target.value)
                                }
                                placeholder="https://company.com"
                                className="w-full"
                            />
                        </div>

                        <div className="space-y-2" style={{ paddingBottom: "16px" }}>
                            <Label
                                htmlFor="companyLinkedIn"
                                className="text-sm font-medium text-gray-700"
                                style={{ color: "#000000" }}
                            >
                                Company LinkedIn
                            </Label>
                            <Input
                                id="companyLinkedIn"
                                value={formData.companyLinkedIn}
                                onChange={(e) =>
                                    handleInputChange("companyLinkedIn", e.target.value)
                                }
                                placeholder="https://linkedin.com/company/..."
                                className="w-full"
                            />
                        </div>
                    </div>

                    {/* Description section */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Label
                                htmlFor="description"
                                className="text-sm font-medium text-gray-700"
                                style={{ color: "#000000" }}
                            >
                                Description
                            </Label>
                            {props.company?.is_byoc && (
                                <Tooltip
                                    content={
                                        <p>This description will be displayed at the bottom of each of your company job listings.</p>
                                    }
                                    side="right"
                                    delayDuration={0}
                                >
                                    <InfoIcon className="h-4 w-4 text-blue-500 cursor-help" />
                                </Tooltip>
                            )}
                        </div>
                        <Textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => handleInputChange("description", e.target.value)}
                            placeholder="Write about your company"
                            className="w-full min-h-[120px] resize-none"
                        />
                    </div>
                </div>
            </SettingsTwoPanelLayout>

            {/* Divider */}
            <hr className="border-gray-200" />

            {/* Departments Section - only for BYOC companies */}
            {props.company && props.company?.is_byoc && (
                <>
                    <SettingCompanyDepartmentSection
                        company={props.company}
                        onCompaniesChange={(companies: SubCompany[]) => {
                            companiesChanges(companies);
                        }}
                    />
                    {/* Divider */}
                    <hr className="border-gray-200" />
                </>
            )}

            {/* Branding Section - only for BYOC companies */}
            {props.company?.is_byoc && (
                <SettingsTwoPanelLayout
                    title="Branding"
                    description="Customize your company's visual identity for candidate-facing interfaces."
                >
                    <div className="space-y-6">
                        {/* Accent Color Generation Section */}
                        <div className="space-y-3" style={{ paddingBottom: "16px" }}>
                            <div className="flex flex-row items-center justify-between">
                                <div className="flex flex-col">
                                    <div className="flex items-center justify-between">
                                        <Label
                                            className="text-sm font-medium text-gray-700"
                                            style={{ color: "#000000" }}
                                        >
                                            Brand Accent Color
                                        </Label>
                                    </div>
                                    <p className="text-sm text-gray-600" style={{ marginTop: "4px" }}>
                                        Select a key color to be used as your brand's visual accent.
                                    </p>
                                </div>

                                <Button
                                    onClick={() => setShowPreviewDialog(true)}
                                    variant="outline"
                                    className="bg-white border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-200 group"
                                >
                                    <EyeIcon className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
                                    <span>Preview as Candidate</span>
                                </Button>
                            </div>

                            <div className="space-y-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <div className="flex flex-wrap gap-3">
                                    {!logoPreview ? (
                                        /* Empty state when no logo is uploaded */
                                        <div
                                            className="w-full py-4 text-center bg-gray-50 rounded-lg border border-dashed border-gray-300"
                                            style={{
                                                backgroundColor: "transparent",
                                                paddingTop: "0px",
                                                paddingBottom: "0px",
                                                border: "0px dashed transparent",
                                            }}
                                        >
                                            <div className="flex items-center justify-center space-x-2">
                                                <PaletteIcon className="h-5 w-5 text-gray-400" />

                                                <p className="text-sm text-gray-500 font-medium">
                                                    Upload a company logo to generate brand colors.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        /* Show color options when logo is uploaded or colors are generated */
                                        <>
                                            {/* Show generated colors if available, otherwise show default colors */}
                                            {(generatedColors.length > 0
                                                ? generatedColors
                                                : [
                                                    "#2563eb", // Blue
                                                    "#7c3aed", // Purple
                                                    "#059669", // Green
                                                    "#dc2626", // Red
                                                    "#ea580c", // Orange
                                                    "#0891b2", // Cyan
                                                ]
                                            ).map((color, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleColorSelect(color)}
                                                    className={`relative w-10 h-10 rounded-full border-3 transition-all duration-200 hover:scale-105 hover:shadow-md ${selectedAccentColor === color &&
                                                        !showCustomColorPicker
                                                        ? "border-white shadow-lg ring-2 ring-gray-800 ring-offset-2"
                                                        : "border-white shadow-sm hover:shadow-md"
                                                        }`}
                                                    style={{ backgroundColor: color }}
                                                    title={color}
                                                >
                                                    {selectedAccentColor === color &&
                                                        !showCustomColorPicker && (
                                                            <CheckIcon className="h-4 w-4 text-white absolute inset-0 m-auto" />
                                                        )}
                                                </button>
                                            ))}
                                        </>
                                    )}
                                </div>

                                {/* Custom Color Input */}
                                <div
                                    className="mt-3"
                                >
                                    <div className="flex items-center space-x-3">
                                        <Label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                            Custom Color:
                                        </Label>
                                        <div id="box-color" className="flex items-center space-x-2 relative">
                                            <div
                                                className="w-8 h-8 rounded border border-gray-300 cursor-pointer flex items-center justify-center"
                                                style={{ backgroundColor: customColor }}
                                                onClick={() => {
                                                    const colorInput = document.createElement("input");
                                                    colorInput.type = "color";
                                                    colorInput.value = customColor;
                                                    colorInput.style.position = "absolute";
                                                    colorInput.style.opacity = "0";
                                                    colorInput.style.width = "100%";
                                                    colorInput.style.height = "100%";
                                                    colorInput.style.cursor = "pointer";
                                                    document.getElementById("box-color")?.appendChild(colorInput);

                                                    setTimeout(() => {
                                                        colorInput.onchange = (e) => {
                                                            handleCustomColorChange(
                                                                (e.target as HTMLInputElement).value
                                                            );
                                                        };
                                                        colorInput.click();
                                                    }, 200)
                                                }}
                                                title={`Current color: ${customColor}`}
                                            ></div>

                                            <Input
                                                type="text"
                                                value={customColor}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (
                                                        /^#[0-9A-Fa-f]{0,6}$/.test(value) ||
                                                        value === ""
                                                    ) {
                                                        handleCustomColorChange(value);
                                                    }
                                                }}
                                                placeholder="#2563eb"
                                                className="w-24 h-8 text-xs font-mono"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Choose any color using the color picker or enter a hex code
                                        (e.g., #2563eb).
                                    </p>
                                </div>
                                {selectedAccentColor && (
                                    <div className="flex items-center space-x-2">
                                        <p className="text-xs text-gray-600">
                                            Selected:{" "}
                                            <span className="font-mono font-medium">
                                                {selectedAccentColor}
                                            </span>
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Show branding toggle */}
                        <div
                            className="flex items-center justify-between p-4 rounded-lg border border-gray-200"
                            style={{
                                paddingTop: "0px",
                                paddingRight: "0px",
                                paddingBottom: "0px",
                                paddingLeft: "0px",
                                border: "0px solid rgb(229, 231, 235)",
                            }}
                        >
                            <div className="space-y-1">
                                <Label
                                    className="text-sm font-medium text-gray-700"
                                    style={{ color: "#000000" }}
                                >
                                    Show branding
                                </Label>
                                <p className="text-sm text-gray-500">
                                    When turned off, the interface will display ourteam's default
                                    branding.
                                </p>
                            </div>
                            <Switch
                                checked={showBranding}
                                onCheckedChange={(checked) => {
                                    setShowBranding(checked)
                                    handleInputChange("show_brand", checked);
                                }}
                                className={`${showBranding ? "bg-green-500 data-[state=checked]:bg-green-500" : "bg-gray-300"}`}
                            />
                        </div>
                    </div>
                </SettingsTwoPanelLayout>
            )}

            {/* Save Changes Button */}
            <div className="flex justify-end pt-4">
                <Button
                    onClick={handleSave}
                    disabled={!hasChanges || isLoadingUserProfile || isLoadingSetting || !isCompanyHasDepartment}
                    className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Save Changes
                </Button>
            </div>

            <PreviewWaitingRoom
                open={showPreviewDialog}
                onOpenChange={setShowPreviewDialog}
                companyLogo={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${logoPreview || 'commons/ourteam_logo_only.png'}`}
                accentColor={customColor}
                companyName={formData.companyName || "Your Company"}
                showBranding={showBranding}
            />
        </div>
    );
}
